import { toRelayDateTime } from '../persistence/relay';
import { haengeAn } from './dateien';
import { meldeFehlerzeile } from './fehlerberichte';
import { istDesktop, istGeraet } from './gleis';
import { stelleOrdnerSicher } from './ordner';

/**
 * Das Dateilog.
 *
 * ⭐ **Am iPad schreibt seit dem 2026-10-18 die native Seite** (`ios/App/App/GeraetelogPlugin.swift`):
 * sie leitet stdout/stderr nach `Documents/log.txt` um, und Capacitor reicht jedes `console.*`
 * dorthin weiter (`loggingBehavior: 'production'`). Eine Zeile hier ist deshalb dort nur ein
 * Konsolenaufruf — schriebe sie zusätzlich selbst, stünde sie doppelt in `log.txt`.
 *
 * Am Desktop ebenso: der Hauptprozess schreibt die Fensterkonsole nach `log.txt` (`desktop/main.cts`).
 * Nur der Browser bleibt beim Tageslog `logs/app/<yyyy-MM-dd>.log`, ohne Rotation.
 *
 * ⚠️ Das Log darf **nie** selbst zum Fehler werden. Scheitert das Schreiben, bleibt es dabei.
 */

export type Stufe = 'log' | 'info' | 'warn' | 'error' | 'debug';

const p = (v: number) => String(v).padStart(2, '0');

export function appLogPath(now = new Date()): string {
  return 'logs/app/' + now.getFullYear() + '-' + p(now.getMonth() + 1) + '-' + p(now.getDate()) + '.log';
}

/**
 * Eine Zeile ins Log — **und auf die Konsole**, damit sie am Kabel in Xcode steht.
 *
 * ⚠️ **Die Konsole zuerst, die Datei danach.** Kein Platz mehr auf dem Gerät ist genau die
 * Lage, in der die Datei nichts mehr aufnimmt — dann bleibt das Kabel der einzige Weg hinaus.
 *
 * `stufe` steuert nur, auf welcher Konsolenfunktion die Zeile herauskommt. In Xcode lässt sich
 * damit nach dem filtern, was zählt.
 */
export async function appendAppLog(text: string, stufe: Stufe = 'info'): Promise<void> {
  try {
    // Das Präfix macht die Zeilen im Xcode-Lärm auffindbar: dort steht alles von Capacitor,
    // WKWebView und CFNetwork in derselben Spalte.
    console[stufe]('[fairexa] ' + text);
  } catch {
    // Eine Konsole, die wirft, darf nicht der Grund sein, warum eine Zeile fehlt.
  }
  if (stufe === 'error') meldeFehlerzeile(text);
  if (istGeraet() || istDesktop()) return;
  try {
    await stelleOrdnerSicher('logs/app');
    await haengeAn(appLogPath(), toRelayDateTime(new Date()) + ' ' + text + '\n');
  } catch {
    // Kein Platz, kein Zugriff — hier endet es. Ein Log, das wirft, hilft niemandem.
  }
}
